import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

interface Props {
  name: string;
  onNameChange: (name: string) => void;
  errorMessage: string;
  isDirty: boolean;
  isNameValid: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export function DriverGroupEditBar({
  name,
  onNameChange,
  errorMessage,
  isDirty,
  isNameValid,
  isSaving,
  onCancel,
  onSave,
}: Readonly<Props>): React.JSX.Element {
  const { t } = useTranslation();
  const canSave: boolean = isDirty && isNameValid;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onNameChange(event.target.value);
  };

  return (
    <div className="flex items-center gap-3.5 bg-card border border-violet-200 border-l-[3px] border-l-violet-600 rounded-lg px-4 py-3.5">
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold tracking-wider uppercase text-violet-600">
          {t('driverGroups.manageMembers')}
        </span>
        <Input
          value={name}
          onChange={handleChange}
          aria-label={t('driverGroups.groupName')}
          className="w-65 h-8.5 bg-card text-base font-semibold"
        />
      </div>
      {errorMessage && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}
      <div className="ml-auto flex items-center gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button onClick={onSave} disabled={isSaving || !canSave}>
          {isSaving && <Loader2 className="animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
